import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { Group } from '@mui/icons-material';
import allocationsService from '../services/allocations';
import batchesService from '../services/batches';
import BatchCreationForm from './BatchCreationForm';

const BatchAssignmentDialog = ({ open, student, onClose, onSuccess }) => {
  const [mode, setMode] = useState('loading'); // 'loading', 'existing', 'new'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideValidation, setOverrideValidation] = useState(null);
  const [pendingOverrideBatchId, setPendingOverrideBatchId] = useState(null);

  // Load batch suggestions
  const [batchSuggestions, setBatchSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
const [allBatches, setAllBatches] = useState([]);
const [loadingAllBatches, setLoadingAllBatches] = useState(false);
const [selectedBatchId, setSelectedBatchId] = useState('');
const [batchFilter, setBatchFilter] = useState('');

  // Load batch suggestions when dialog opens
  useEffect(() => {
    if (student?.id && open) {
      setLoadingSuggestions(true);
      allocationsService.suggestBatches(student.id, { withinMonths: 2 })
        .then((response) => {
          const suggestions = response?.data?.suggestions || response?.suggestions;
          setBatchSuggestions(suggestions);
          if (suggestions?.mode === 'existing_batches' && (suggestions?.suggestions?.length || 0) > 0) {
            setMode('existing');
            setShowCreateForm(false);
          } else {
            setMode('new');
            setShowCreateForm(false); // keep creation collapsed by default even when no matches
          }
        })
        .catch((error) => {
          console.error('Error loading batch suggestions:', error);
          setMode('new');
        })
        .finally(() => {
          setLoadingSuggestions(false);
        });
    }
  }, [student?.id, open]);

  // Load all batches for manual assignment dropdown (always visible)
  useEffect(() => {
    if (open) {
      setLoadingAllBatches(true);
      batchesService.getAll({ page: 1, limit: 200 })
        .then((res) => setAllBatches(res.data?.data || res.data || []))
        .catch((error) => console.error('Error loading batches list:', error))
        .finally(() => setLoadingAllBatches(false));
    }
  }, [open]);

  const prefillFromStudent = student ? {
    age_group: student.age_group || '',
    batch_type: student.class_type_preference || 'group',
    is_inclusive: student.student_type === 'autistic' || student.student_type === 'special_needs'
  } : null;

  const handleBatchCreatedFromStudent = async (createdBatch) => {
    if (!student?.id || !createdBatch?.id) return;
    setShowCreateForm(false);
    setLoading(true);
    setError('');
    try {
      await allocationsService.assignStudent({
        studentId: student.id,
        batchId: createdBatch.id,
        assignmentType: 'manual',
        adminNotes: 'Assigned during batch creation from student'
      });
      onSuccess();
      handleClose();
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      const validation = data?.validation;
      if (status === 400 && validation && validation.isValid === false) {
        setOverrideValidation(validation);
        setPendingOverrideBatchId(createdBatch.id);
        setOverrideDialogOpen(true);
        setError('Assignment validation failed. Review mismatches and choose whether to override.');
      } else {
        setError(data?.message || 'Batch created but failed to assign student. Please assign manually.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAssignToExisting = async (batchId, options = {}) => {
    setLoading(true);
    setError('');
    
    try {
      await allocationsService.assignStudent({
        studentId: student.id,
        batchId,
        assignmentType: 'manual',
        adminNotes: 'Assigned via batch assignment dialog',
        ...(options.forceAssign ? { forceAssign: true } : {})
      });
      
      onSuccess();
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      const validation = data?.validation;

      if (status === 400 && validation && validation.isValid === false && !options.forceAssign) {
        // Show mismatch points and ask for override confirmation
        setOverrideValidation(validation);
        setPendingOverrideBatchId(batchId);
        setOverrideDialogOpen(true);
        setError('Assignment validation failed. Review mismatches and choose whether to override.');
      } else {
        setError(data?.message || 'Failed to assign student to batch');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAssignFromDropdown = () => {
    if (!selectedBatchId) {
      setError('Please select a batch');
      return;
    }
    handleAssignToExisting(selectedBatchId);
  };

  const handleConfirmOverride = async () => {
    if (!pendingOverrideBatchId) return;
    setOverrideDialogOpen(false);
    await handleAssignToExisting(pendingOverrideBatchId, { forceAssign: true });
  };

  const handleCancelOverride = () => {
    setOverrideDialogOpen(false);
    setPendingOverrideBatchId(null);
    setOverrideValidation(null);
  };

  const handleClose = () => {
    setMode('loading');
    setSelectedBatchId('');
    setShowCreateForm(false);
    setError('');
    setOverrideDialogOpen(false);
    setOverrideValidation(null);
    setPendingOverrideBatchId(null);
    onClose();
  };

  if (!student) return null;

  return (
  <>
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Group />
          Assign Batch - {student.first_name} {student.last_name}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Assign to any existing batch (always shown) */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Assign to Any Existing Batch
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                label="Filter batches"
                placeholder="Type to filter by batch or teacher"
                value={batchFilter}
                onChange={(e) => setBatchFilter(e.target.value)}
                sx={{ mb: 1 }}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Select Batch</InputLabel>
                <Select
                  value={selectedBatchId}
                  label="Select Batch"
                  onChange={(e) => setSelectedBatchId(e.target.value)}
                  disabled={loadingAllBatches}
                >
                  {loadingAllBatches && <MenuItem value=""><em>Loading...</em></MenuItem>}
                  {!loadingAllBatches && allBatches.length === 0 && (
                    <MenuItem value="">
                      <em>No batches found</em>
                    </MenuItem>
                  )}
                  {!loadingAllBatches && allBatches
                    .filter((b) => {
                      if (!batchFilter.trim()) return true;
                      const q = batchFilter.toLowerCase();
                      return (
                        (b.name || '').toLowerCase().includes(q) ||
                        (b.teacher_name || '').toLowerCase().includes(q)
                      );
                    })
                    .map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name} {b.teacher_name ? `• ${b.teacher_name}` : ''} (Cap {b.current_capacity}/{b.max_capacity})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleAssignFromDropdown}
                disabled={loading || !selectedBatchId}
              >
                {loading ? 'Assigning...' : 'Assign to Batch'}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Override confirmation dialog */}
        <Dialog
          open={overrideDialogOpen}
          onClose={handleCancelOverride}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Override assignment mismatches?</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This batch doesn&apos;t match the student&apos;s criteria. You can still assign, but please review the mismatches below.
            </Alert>

            {overrideValidation?.warnings?.length ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Warnings
                </Typography>
                <List dense>
                  {overrideValidation.warnings.map((w, idx) => (
                    <ListItem key={`w-${idx}`} sx={{ py: 0.25 }}>
                      <ListItemText primary={w} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : null}

            {overrideValidation?.validationResults ? (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Mismatch details
                </Typography>
                <List dense>
                  {Object.entries(overrideValidation.validationResults).map(([key, result]) => {
                    const passed = !!result?.passed;
                    const details = result?.details || '';
                    return (
                      <React.Fragment key={key}>
                        <ListItem sx={{ py: 0.25 }}>
                          <ListItemText
                            primary={`${passed ? '✅' : '❌'} ${key}`}
                            secondary={details}
                          />
                        </ListItem>
                        <Divider component="li" />
                      </React.Fragment>
                    );
                  })}
                </List>
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelOverride} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleConfirmOverride} variant="contained" color="warning" disabled={loading}>
              Override &amp; Assign
            </Button>
          </DialogActions>
        </Dialog>

        {loadingSuggestions ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : mode === 'existing' ? (
          <Box>
                         <Typography variant="h5" gutterBottom>
               Available Existing Batches
             </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Found {batchSuggestions?.suggestions?.length || 0} matching batches
            </Typography>
            
            {batchSuggestions?.suggestions?.map((batch) => (
              <Card key={batch.batchId} sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" component="div">{batch.batchName}</Typography>
                    <Box display="flex" gap={1}>
                      <Chip 
                        label={`${(batch.matchScore * 100).toFixed(1)}% Match`}
                        color={batch.matchScore > 0.8 ? 'success' : batch.matchScore > 0.6 ? 'warning' : 'default'}
                        size="small"
                      />
                      <Chip 
                        label={batch.isPerfectMatch ? 'Perfect time match' : 'Partial time match'}
                        color={batch.isPerfectMatch ? 'success' : 'warning'}
                        size="small"
                        variant={batch.isPerfectMatch ? 'filled' : 'outlined'}
                      />
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Teacher: {batch.teacherName}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Capacity: {batch.currentCapacity}/{batch.maxCapacity}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Type: {batch.batchType} • Age: {batch.ageGroupMin}-{batch.ageGroupMax}
                  </Typography>
                  
                  <Button
                    variant="contained"
                    onClick={() => handleAssignToExisting(batch.batchId)}
                    disabled={batch.currentCapacity >= batch.maxCapacity || loading}
                    fullWidth
                  >
                    {batch.currentCapacity >= batch.maxCapacity ? 'Batch Full' : 'Assign to This Batch'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <Box>
            {mode === 'existing' && (
              <Box sx={{ mb: 2 }}>
                <Button variant="outlined" onClick={() => setShowCreateForm(!showCreateForm)}>
                  {showCreateForm ? 'Hide Create New Batch' : 'Create New Batch'}
                </Button>
              </Box>
            )}
            <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
              {mode === 'new' && (
                <Alert severity="info" sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight="medium">
                    No matching existing batches found for this student.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    The system searched for batches within the last 2 months that match the student's criteria.
                  </Typography>
                </Alert>
              )}
              <Button variant="contained" onClick={() => setShowCreateForm(!showCreateForm)}>
                {showCreateForm ? 'Hide Create New Batch' : 'Create New Batch'}
              </Button>
            </Box>

            {showCreateForm && (
              <Typography variant="body2" color="text.secondary">
                Use the Create New Batch form that opened — same as Batch Management. It is pre-filled with this student&apos;s age group, batch type, and inclusive setting. After creating the batch, the student will be assigned to it automatically.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>

    <BatchCreationForm
      open={showCreateForm}
      onClose={() => setShowCreateForm(false)}
      onSuccess={handleBatchCreatedFromStudent}
      prefill={prefillFromStudent}
      mode="create"
    />
  </>
  );
};

export default BatchAssignmentDialog;
